import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Divider,
  message
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  HeartOutlined,
  GoogleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  SafetyOutlined,
  ArrowLeftOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { Heart, Shield, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DisplayCards from './display-cards';
import MainHeader from '../MainHeader';
import Footer from '../Footer';
import { useTheme } from '../ParticleBackground';
import { useAuth } from '../../contexts/AuthContext';
import useResponsive from '../../hooks/useResponsive';
import { sendOtp, verifyOtp } from '../../services/otpService';
import { usersDB } from '../../services/firebase/database';

const { Title, Text, Link } = Typography;

const AnimatedSignIn = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // OTP verification states
  const [registrationStep, setRegistrationStep] = useState('email'); // 'email' | 'otp' | 'password'
  const [otpValue, setOtpValue] = useState('');
  const [otpHash, setOtpHash] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailError, setEmailError] = useState(''); // Inline email error message
  const cooldownTimerRef = useRef(null);

  const { theme } = useTheme();
  const { login, loginWithGoogle, register, error, clearError, forgotPassword, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  useEffect(() => {
    setMounted(true);

    // Clear any previous auth errors when component mounts
    if (clearError) {
      clearError();
    }
  }, [clearError]);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Check if there's a stored intended destination
      const intendedDestination = localStorage.getItem('intendedDestination');
      if (intendedDestination) {
        localStorage.removeItem('intendedDestination');
        navigate(intendedDestination);
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Reset registration step when switching between login/register
  useEffect(() => {
    if (isLogin) {
      setRegistrationStep('email');
      setOtpValue('');
      setOtpHash('');
      setOtpExpiry(0);
      setResendCooldown(0);
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    }
  }, [isLogin]);

  // Start cooldown timer
  const startCooldownTimer = useCallback(() => {
    setResendCooldown(30);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Handle sending OTP
  const handleSendOtp = async () => {
    // Validate email
    if (!formData.email) {
      message.error('Please enter your email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      message.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Check if user already exists in Firestore
      const userExists = await usersDB.checkEmailExists(formData.email);
      if (userExists) {
        setEmailError('This email is already registered. Please login instead.');
        setLoading(false);
        return;
      }

      // Send OTP
      const result = await sendOtp(formData.email);
      if (result.success) {
        setOtpHash(result.hash);
        setOtpExpiry(result.expiry);
        setRegistrationStep('otp');
        startCooldownTimer();
        message.success('Verification code sent to your email!');
      } else {
        message.error(result.error || 'Failed to send verification code');
      }
    } catch (err) {
      message.error('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const result = await sendOtp(formData.email);
      if (result.success) {
        setOtpHash(result.hash);
        setOtpExpiry(result.expiry);
        setOtpValue('');
        startCooldownTimer();
        message.success('New verification code sent!');
      } else {
        message.error(result.error || 'Failed to resend verification code');
      }
    } catch (err) {
      message.error('Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle verify OTP
  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) {
      message.error('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOtp(formData.email, otpValue, otpHash, otpExpiry);
      if (result.success) {
        setRegistrationStep('password');
        message.success('Email verified successfully!');
      } else {
        if (result.code === 'OTP_EXPIRED') {
          message.error('Verification code expired. Please request a new one.');
        } else {
          message.error('Invalid verification code. Please try again.');
        }
      }
    } catch (err) {
      message.error('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle use another email
  const handleUseAnotherEmail = () => {
    setRegistrationStep('email');
    setOtpValue('');
    setOtpHash('');
    setOtpExpiry(0);
    setResendCooldown(0);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
  };

  // Therapy-themed cards for the display
  const therapyCards = [
    {
      icon: <Heart style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "Mental Wellness",
      description: "Professional therapy services",
      date: "Available 24/7",
      className: "card-1",
      style: {
        gridArea: 'stack',
        transform: 'translateY(0px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        filter: 'grayscale(100%)',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    {
      icon: <Shield style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "Safe Space",
      description: "Confidential & secure sessions",
      date: "HIPAA Compliant",
      className: "card-2",
      style: {
        gridArea: 'stack',
        transform: 'translateX(64px) translateY(40px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        filter: 'grayscale(100%)',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    {
      icon: <Users style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "Expert Care",
      description: "Licensed professionals",
      date: "15+ Years Experience",
      className: "card-3",
      style: {
        gridArea: 'stack',
        transform: 'translateX(128px) translateY(80px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
  ];

  // Theme-aware styles for black/white design
  const getSignInThemeStyles = () => {
    if (theme === 'dark') {
      return {
        pageBackground: '#000000',
        gridColor: 'rgba(255, 255, 255, 0.1)',
        leftPanel: { background: 'transparent' },
        rightPanel: { background: 'transparent' },
        cardBackground: 'rgba(255, 255, 255, 0.05)',
        cardBorder: 'rgba(255, 255, 255, 0.1)',
        cardBackdrop: 'blur(20px)',
        titleColor: '#ffffff',
        subtitleColor: '#a0a0a0',
        labelColor: '#e0e0e0',
        inputBackground: 'rgba(255, 255, 255, 0.05)',
        inputBorder: 'rgba(255, 255, 255, 0.2)',
        inputFocusBorder: '#ffffff',
        inputText: '#ffffff',
        inputPlaceholder: '#a0a0a0',
        primaryButton: '#ffffff',
        primaryButtonText: '#000000',
        primaryButtonHover: '#f0f0f0',
        secondaryButton: 'transparent',
        secondaryButtonBorder: 'rgba(255, 255, 255, 0.3)',
        secondaryButtonText: '#ffffff',
        toggleBackground: 'rgba(255, 255, 255, 0.1)',
        toggleActive: '#ffffff',
        toggleActiveText: '#000000',
        toggleInactive: '#a0a0a0',
      };
    } else {
      return {
        pageBackground: '#ffffff',
        gridColor: 'rgba(0, 0, 0, 0.1)',
        leftPanel: { background: 'transparent' },
        rightPanel: { background: 'transparent' },
        cardBackground: 'rgba(0, 0, 0, 0.02)',
        cardBorder: 'rgba(0, 0, 0, 0.1)',
        cardBackdrop: 'blur(20px)',
        titleColor: '#000000',
        subtitleColor: '#666666',
        labelColor: '#333333',
        inputBackground: 'rgba(0, 0, 0, 0.02)',
        inputBorder: 'rgba(0, 0, 0, 0.2)',
        inputFocusBorder: '#000000',
        inputText: '#000000',
        inputPlaceholder: '#666666',
        primaryButton: '#000000',
        primaryButtonText: '#ffffff',
        primaryButtonHover: '#333333',
        secondaryButton: 'transparent',
        secondaryButtonBorder: 'rgba(0, 0, 0, 0.3)',
        secondaryButtonText: '#000000',
        toggleBackground: 'rgba(0, 0, 0, 0.05)',
        toggleActive: '#000000',
        toggleActiveText: '#ffffff',
        toggleInactive: '#666666',
      };
    }
  };

  const signInStyles = getSignInThemeStyles();

  // --- STYLES ---
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      background: signInStyles.pageBackground,
      position: 'relative',
      // overflow: 'hidden', // REMOVE this to let grid cover both panels
    },
    gridContainer: {
      position: 'fixed',
      inset: 0,
      zIndex: 1,
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      display: 'none', // Hide the grid container
    },
    container: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      paddingTop: '20px',
      position: 'relative',
      zIndex: 2
    },
    leftPanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px 40px',
      position: 'relative',
      minHeight: 'calc(100vh - 48px)',
      ...signInStyles.leftPanel
    },
    rightPanel: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px 40px',
      minHeight: 'calc(100vh - 20px)',
      ...signInStyles.rightPanel
    },
    signInCard: {
      width: '100%',
      maxWidth: '420px',
      borderRadius: '24px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
      background: signInStyles.cardBackground,
      backdropFilter: signInStyles.cardBackdrop,
      border: `1px solid ${signInStyles.cardBorder}`,
      overflow: 'hidden',
      transform: mounted ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
      opacity: mounted ? 1 : 0,
      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
      minHeight: 480, // ENSURE consistent card height for both login & signup
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    header: {
      textAlign: 'center',
      marginBottom: isLogin ? '16px' : '12px',
      padding: isLogin ? '16px 32px 0' : '12px 32px 0'
    },
    logoContainer: {
      width: '64px',
      height: '64px',
      background: theme === 'dark' ? '#ffffff' : '#000000',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      boxShadow: `0 8px 32px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
      animation: mounted ? 'float 3s ease-in-out infinite' : 'none'
    },
    brandName: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: signInStyles.titleColor,
      marginBottom: '8px'
    },
    subtitle: {
      color: signInStyles.subtitleColor,
      fontSize: '15px'
    },
    toggleContainer: {
      display: 'flex',
      marginBottom: '28px',
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${signInStyles.cardBorder}`,
      background: signInStyles.toggleBackground
    },
    toggleButton: {
      flex: 1,
      border: 'none',
      padding: '14px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'transparent',
      color: signInStyles.toggleInactive
    },
    toggleButtonActive: {
      background: signInStyles.toggleActive,
      color: signInStyles.toggleActiveText,
      boxShadow: `0 4px 12px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`
    },
    formContainer: {
      padding: isLogin ? '0 32px 20px' : '0 32px 12px'
    },
    inputGroup: {
      marginBottom: isLogin ? '16px' : '12px',
      transform: mounted ? 'translateX(0)' : 'translateX(30px)',
      opacity: mounted ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '14px',
      fontWeight: '500',
      color: signInStyles.labelColor
    },
    input: {
      borderRadius: '10px',
      height: '48px',
      fontSize: '15px',
      border: `2px solid ${signInStyles.inputBorder}`,
      background: signInStyles.inputBackground,
      color: signInStyles.inputText,
      transition: 'all 0.3s ease'
    },
    submitButton: {
      width: '100%',
      height: '44px',
      fontSize: '15px',
      fontWeight: '600',
      borderRadius: '10px',
      border: 'none',
      background: signInStyles.primaryButton,
      color: signInStyles.primaryButtonText,
      boxShadow: `0 4px 20px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
      transition: 'all 0.3s ease',
      marginBottom: '12px'
    },
    socialButton: {
      height: '44px',
      borderRadius: '10px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      border: `2px solid ${signInStyles.secondaryButtonBorder}`,
      background: signInStyles.secondaryButton,
      color: signInStyles.secondaryButtonText
    }
  };

  // Add keyframe animations
  useEffect(() => {
    if (!document.getElementById('signin-enhanced-styles')) {
      const style = document.createElement('style');
      style.id = 'signin-enhanced-styles';
      style.textContent = `
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .signin-container {
            flex-direction: column !important;
          }
          .signin-left-panel {
            min-height: 50vh !important;
            padding: 40px 20px !important;
          }
          .signin-right-panel {
            flex: 1 !important;
            padding: 20px !important;
            min-height: 50vh !important;
          }
        }
        .input-group-1 { transition-delay: 0.1s; }
        .input-group-2 { transition-delay: 0.2s; }
        .input-group-3 { transition-delay: 0.3s; }
        .input-group-4 { transition-delay: 0.4s; }
        .signin-input:focus {
          border-color: ${signInStyles.inputFocusBorder} !important;
          box-shadow: 0 0 0 3px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} !important;
        }
        .signin-submit-button:hover {
          transform: translateY(-2px) !important;
          background: ${signInStyles.primaryButtonHover} !important;
        }
        .signin-social-button:hover {
          transform: translateY(-1px) !important;
          border-color: ${signInStyles.inputFocusBorder} !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('signin-enhanced-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [theme, signInStyles, isLogin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      message.error('Please fill in all required fields');
      return false;
    }
    if (!isLogin) {
      if (!formData.name) {
        message.error('Please enter your full name');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        message.error('Passwords do not match');
        return false;
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      message.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isLogin) {
        // Login with email and password
        await login(formData.email, formData.password);
        message.success('Welcome back!');

        // Check for intended service booking
        const intendedBooking = sessionStorage.getItem('intendedServiceBooking');
        if (intendedBooking) {
          sessionStorage.removeItem('intendedServiceBooking');
          const service = JSON.parse(intendedBooking);
          // Service is already cleaned (no React elements)
          navigate('/booking', {
            state: {
              service: service,
              selectedServices: [service]
            }
          });
        } else {
          navigate('/'); // Redirect to home page after login
        }
      } else {
        // Register with email and password
        await register(formData.email, formData.password, formData.name);
        message.success('Account created successfully!');
        navigate('/'); // Redirect to home page after registration
      }
    } catch (error) {
      // Check if email already exists
      if (error.code === 'auth/email-already-in-use') {
        setEmailError('This email is already registered. Please login instead.');
        setRegistrationStep('email'); // Go back to email step to show the error
      } else {
        message.error(error.message || 'An error occurred during authentication');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);

      const result = await loginWithGoogle();

      if (result) {
        message.success('Signed in with Google successfully!');

        // Check for intended service booking
        const intendedBooking = sessionStorage.getItem('intendedServiceBooking');
        if (intendedBooking) {
          sessionStorage.removeItem('intendedServiceBooking');
          try {
            const service = JSON.parse(intendedBooking);
            navigate('/booking', {
              state: {
                service: service,
                selectedServices: [service]
              }
            });
          } catch (error) {
            console.error('Error parsing intended booking:', error);
            navigate('/');
          }
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      message.error(error.message || 'An error occurred during Google sign-in');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      message.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      message.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await forgotPassword(formData.email);
      message.success('Password reset email sent. Please check your inbox.');
    } catch (error) {
      message.error(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: theme === 'dark' ? '#000000' : '#ffffff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
            borderTopColor: theme === 'dark' ? '#ffffff' : '#000000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <Text style={{
            color: theme === 'dark' ? '#ffffff' : '#000000',
            fontSize: '16px'
          }}>
            Checking authentication...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {!isMobile && <MainHeader />}
      <div style={styles.container} className="signin-container">
        {/* Left Panel - Display Cards */}
        <div style={{
          ...styles.leftPanel,
          display: isMobile ? 'none' : 'flex' // Hide on mobile
        }} className="signin-left-panel">
          <div style={{
            marginBottom: '60px',
            textAlign: 'center',
            zIndex: 2,
            position: 'absolute',
            top: '10%', // Moved up from 15% to 10%
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%'
          }}>
            <Title level={1} style={{
              color: signInStyles.titleColor,
              fontSize: '42px',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Welcome to EkahHealth
            </Title>
            <Text style={{
              color: signInStyles.subtitleColor,
              fontSize: '18px'
            }}>
              Your mental wellness journey starts here
            </Text>
          </div>
          <div style={{
            zIndex: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            top: '30%', // Changed from bottom: 45% to top: 30%
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <DisplayCards cards={therapyCards} />
          </div>
        </div>
        {/* Right Panel - Sign In Form */}
        <div style={styles.rightPanel} className="signin-right-panel">
          <Card style={styles.signInCard} bordered={false}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.logoContainer}>
                <HeartOutlined style={{
                  fontSize: '28px',
                  color: theme === 'dark' ? '#000000' : '#ffffff'
                }} />
              </div>
              <div style={styles.brandName}>EkahHealth</div>
              <Text style={styles.subtitle}>
                Welcome to EkahHealth
              </Text>
            </div>
            <div style={styles.formContainer}>
              {/* Toggle Buttons */}
              <div style={styles.toggleContainer}>
                <button
                  style={{
                    ...styles.toggleButton,
                    ...(isLogin ? styles.toggleButtonActive : {})
                  }}
                  onClick={() => setIsLogin(true)}
                >
                  Login
                </button>
                <button
                  style={{
                    ...styles.toggleButton,
                    ...(!isLogin ? styles.toggleButtonActive : {})
                  }}
                  onClick={() => setIsLogin(false)}
                >
                  Register
                </button>
              </div>
              {/* Name Field (Sign Up Only - Step 1: Email) */}
              {!isLogin && registrationStep === 'email' && (
                <div style={styles.inputGroup} className="input-group-1">
                  <Input
                    prefix={<UserOutlined style={{ color: signInStyles.subtitleColor }} />}
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="signin-input"
                    style={styles.input}
                  />
                </div>
              )}
              {/* Email Field - Always show for login, only for Step 1 in registration */}
              {(isLogin || registrationStep === 'email') && (
                <div style={styles.inputGroup} className="input-group-2">
                  <Input
                    prefix={<MailOutlined style={{ color: signInStyles.subtitleColor }} />}
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      setEmailError(''); // Clear error when user types
                    }}
                    className="signin-input"
                    style={{
                      ...styles.input,
                      borderColor: emailError ? '#ef4444' : styles.input.borderColor
                    }}
                    status={emailError ? 'error' : ''}
                  />
                  {/* Inline email error message */}
                  {emailError && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '13px',
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ⚠️ {emailError}
                    </div>
                  )}
                </div>
              )}

              {/* OTP Verification Step (Step 2) */}
              {!isLogin && registrationStep === 'otp' && (
                <>
                  <div style={{
                    padding: '16px',
                    marginBottom: '20px',
                    borderRadius: '12px',
                    background: theme === 'dark' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.08)',
                    border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                    textAlign: 'center'
                  }}>
                    <SafetyOutlined style={{ fontSize: '24px', color: '#667eea', marginBottom: '8px' }} />
                    <Text style={{ display: 'block', color: signInStyles.labelColor, fontSize: '14px' }}>
                      We've sent a 6-digit code to
                    </Text>
                    <Text strong style={{ color: signInStyles.titleColor, fontSize: '15px' }}>
                      {formData.email}
                    </Text>
                  </div>
                  <div style={styles.inputGroup}>
                    <Input
                      prefix={<SafetyOutlined style={{ color: signInStyles.subtitleColor }} />}
                      placeholder="Enter 6-digit code"
                      value={otpValue}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpValue(value);
                      }}
                      className="signin-input"
                      style={{
                        ...styles.input,
                        letterSpacing: '8px',
                        fontSize: '20px',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}
                      maxLength={6}
                    />
                  </div>
                  <Button
                    type="primary"
                    loading={loading}
                    onClick={handleVerifyOtp}
                    className="signin-submit-button"
                    style={styles.submitButton}
                  >
                    Verify Code
                  </Button>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0}
                      loading={loading && resendCooldown === 0}
                      style={{
                        color: resendCooldown > 0 ? signInStyles.subtitleColor : signInStyles.inputFocusBorder,
                        fontSize: '14px'
                      }}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                    </Button>
                    <Button
                      type="text"
                      icon={<ArrowLeftOutlined />}
                      onClick={handleUseAnotherEmail}
                      style={{ color: signInStyles.inputFocusBorder, fontSize: '14px' }}
                    >
                      Use Another Email
                    </Button>
                  </div>
                </>
              )}

              {/* Password Fields - Only for Login OR Step 3 (password) in registration */}
              {(isLogin || registrationStep === 'password') && (
                <>
                  {/* Show verified email badge for registration step 3 */}
                  {!isLogin && registrationStep === 'password' && (
                    <div style={{
                      padding: '12px 16px',
                      marginBottom: '16px',
                      borderRadius: '8px',
                      background: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.08)',
                      border: `1px solid ${theme === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <SafetyOutlined style={{ color: '#22c55e', fontSize: '16px' }} />
                      <Text style={{ color: '#22c55e', fontSize: '13px' }}>
                        Email verified: <strong>{formData.email}</strong>
                      </Text>
                    </div>
                  )}
                  <div style={styles.inputGroup} className="input-group-3">
                    <Input.Password
                      prefix={<LockOutlined style={{ color: signInStyles.subtitleColor }} />}
                      placeholder="Password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="signin-input"
                      style={styles.input}
                      iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    />
                  </div>
                  {/* Confirm Password Field (Sign Up Only - Step 3) */}
                  {!isLogin && (
                    <div style={styles.inputGroup} className="input-group-4">
                      <Input.Password
                        prefix={<LockOutlined style={{ color: signInStyles.subtitleColor }} />}
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className="signin-input"
                        style={styles.input}
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Forgot Password (Login Only) */}
              {isLogin && (
                <div style={{ textAlign: 'right', marginBottom: '18px' }}>
                  <Link onClick={handleForgotPassword} style={{ fontSize: '14px', color: signInStyles.inputFocusBorder }}>
                    Forgot the password?
                  </Link>
                </div>
              )}
              {/* Error display */}
              {error && (
                <div style={{
                  padding: '12px',
                  marginBottom: '18px',
                  borderRadius: '8px',
                  backgroundColor: theme === 'dark' ? 'rgba(254, 226, 226, 0.1)' : 'rgba(254, 226, 226, 0.5)',
                  borderLeft: '4px solid #ef4444',
                  color: theme === 'dark' ? '#fca5a5' : '#b91c1c'
                }}>
                  {error}
                </div>
              )}

              {/* Submit Button - Login OR Registration Step 1 (Send OTP) OR Step 3 (Register) */}
              {(isLogin || registrationStep === 'email' || registrationStep === 'password') && registrationStep !== 'otp' && (
                <Button
                  type="primary"
                  loading={loading}
                  onClick={isLogin ? handleSubmit : (registrationStep === 'email' ? handleSendOtp : handleSubmit)}
                  className="signin-submit-button"
                  style={styles.submitButton}
                >
                  {isLogin ? 'Login' : (registrationStep === 'email' ? 'Send Verification Code' : 'Create Account')}
                </Button>
              )}

              {/* Divider - Only for Login or Registration Step 1 */}
              {(isLogin || registrationStep === 'email') && (
                <>
                  <Divider style={{ margin: '18px 0', color: signInStyles.subtitleColor }}>
                    OR
                  </Divider>
                  {/* Google Sign In */}
                  <Button
                    icon={<GoogleOutlined />}
                    className="signin-social-button"
                    style={{ ...styles.socialButton, width: '100%', marginBottom: '18px' }}
                    onClick={handleGoogleSignIn}
                    loading={loading}
                  >
                    Continue with Google
                  </Button>
                </>
              )}
              {/* Terms & Privacy (Sign Up Only - Step 1) */}
              {!isLogin && registrationStep === 'email' && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <Text style={{ fontSize: '13px', color: signInStyles.subtitleColor }}>
                    By creating an account, you agree to our{' '}
                    <Link href="#" style={{ color: signInStyles.inputFocusBorder }}>Terms of Service</Link> and{' '}
                    <Link href="#" style={{ color: signInStyles.inputFocusBorder }}>Privacy Policy</Link>
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export { AnimatedSignIn };